import React, {
  useState,
  useMemo,
  useEffect,
  useRef,
} from 'react'
import {
  GoogleMap, Polyline, Marker, useJsApiLoader, InfoWindow,
} from '@react-google-maps/api'
import type { Libraries } from '@googlemaps/js-api-loader'
import { getFromNowAsText } from '../../helpers/timeHelper'
import { firstMapPinIcon } from '../../icons'
import { GOOGLE_MAP_API_KEY } from '../../constants/Constants'
import incrementGoogleMapsLoadCount from '../../utils/googleMapsLoadCounter'
import './DetailMamorioMap.css'

type Props = {
  date: string
  jt808Logs: IJT808Log[]
  updateGpsTrackerLogs: (arg: string) => Promise<void>
  downloadLogs: () => void
  currentStatus?: IGpsTrackerStatus
  detailHistoryEnabled?: boolean
}

const DetailGpsTrackerMap = ({
  date,
  jt808Logs,
  updateGpsTrackerLogs,
  downloadLogs,
  currentStatus,
  detailHistoryEnabled = false,
}: Props) => {
  const [selectedLogIndex, setSelectedLogIndex] = useState(0)
  const [libraries] = useState<Libraries>(['visualization'])
  const [size, setSize] = useState<undefined | google.maps.Size>(undefined)
  const [showInfoWindow, setShowInfoWindow] = useState<boolean>(false)
  const [infoWindowPosition, setInfoWindowPosition] = useState<{ lat: number; lng: number } | null>(null)

  const {
    isLoaded,
    loadError,
  } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAP_API_KEY,
    libraries,
  })
  const hasCountedLoadRef = useRef(false)

  const infoWindowOptions = {
    pixelOffset: size,
  }
  const updateOffsetSize = () => setSize(new window.google.maps.Size(0, 0))

  // 初期表示用のステータス座標
  const statusCoordinate = useMemo(() => {
    if (currentStatus && currentStatus.latitude && currentStatus.longitude) {
      return {
        lat: currentStatus.latitude,
        lng: currentStatus.longitude,
        timestamp: currentStatus.tracked_at,
        speed: currentStatus.speed,
      }
    }
    return null
  }, [currentStatus])

  // JT808ログから座標データを抽出
  const coordinates = useMemo(() => {
    const coords = jt808Logs
      .filter((log) => log.latitude && log.longitude)
      .map((log) => ({
        lat: parseFloat(log.latitude),
        lng: parseFloat(log.longitude),
        timestamp: log.timestamp,
        speed: log.speed,
      }))
      .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())

    // JT808ログがない場合はステータスのみを使用
    if (coords.length === 0 && statusCoordinate) {
      return [statusCoordinate]
    }

    // JT808ログがある場合はステータスも含める
    if (statusCoordinate) {
      coords.push(statusCoordinate)
    }

    return coords
  }, [jt808Logs, statusCoordinate])

  const mapCenter = useMemo(() => {
    // coordinatesがある場合は最新位置を使用
    if (coordinates.length > 0) {
      const latest = coordinates[coordinates.length - 1]
      if (latest) {
        return { lat: latest.lat, lng: latest.lng }
      }
    }
    // coordinatesがなくてもstatusCoordinateがある場合はそれを使用
    if (statusCoordinate) {
      return { lat: statusCoordinate.lat, lng: statusCoordinate.lng }
    }
    return { lat: 35.6762, lng: 139.6503 } // デフォルト: 東京
  }, [coordinates, statusCoordinate])

  const polylinePath = useMemo(() => coordinates.map((coord) => ({ lat: coord.lat, lng: coord.lng })), [coordinates])

  // slider関連の計算
  const { minTimestamp, maxTimestamp } = useMemo(() => {
    if (coordinates.length === 0) return { minTimestamp: 0, maxTimestamp: 0 }
    const timestamps = coordinates.map((coord) => new Date(coord.timestamp).getTime())
    const min = Math.min(...timestamps)
    const max = Math.max(...timestamps)
    return { minTimestamp: min, maxTimestamp: max }
  }, [coordinates])

  const activeIndex = useMemo(() => {
    if (coordinates.length === 0) return null
    return Math.min(selectedLogIndex, coordinates.length - 1)
  }, [coordinates, selectedLogIndex])

  const activeCoordinate = useMemo(() => {
    if (activeIndex === null) return null
    return coordinates[activeIndex]
  }, [activeIndex, coordinates])

  const sliderValue = useMemo(() => {
    if (!activeCoordinate) return 0
    return new Date(activeCoordinate.timestamp).getTime()
  }, [activeCoordinate])

  const sliderPercentage = useMemo(() => {
    if (minTimestamp === maxTimestamp) return 0
    return Math.min(((sliderValue - minTimestamp) / (maxTimestamp - minTimestamp)) * 100, 88)
  }, [sliderValue, minTimestamp, maxTimestamp])

  const sliderLabel = useMemo(() => {
    if (activeCoordinate) {
      return getFromNowAsText(activeCoordinate.timestamp)
    }
    return ''
  }, [activeCoordinate])

  const handleMarkerClick = (index: number) => {
    setSelectedLogIndex(index)
    const coord = coordinates[index]
    if (coord) {
      setInfoWindowPosition({ lat: coord.lat, lng: coord.lng })
      setShowInfoWindow(true)
    }
  }

  // 初期表示時に最新ログを選択
  useEffect(() => {
    if (coordinates.length > 0) {
      const latestIndex = coordinates.length - 1
      setSelectedLogIndex(latestIndex)
      const latest = coordinates[latestIndex]
      if (latest) {
        setInfoWindowPosition({ lat: latest.lat, lng: latest.lng })
        setShowInfoWindow(true)
      }
      return
    }

    if (statusCoordinate) {
      setSelectedLogIndex(0)
      setInfoWindowPosition({ lat: statusCoordinate.lat, lng: statusCoordinate.lng })
      setShowInfoWindow(true)
      return
    }

    setSelectedLogIndex(0)
    setShowInfoWindow(false)
    setInfoWindowPosition(null)
  }, [coordinates, statusCoordinate])

  useEffect(() => {
    if (!isLoaded || hasCountedLoadRef.current) {
      return
    }
    incrementGoogleMapsLoadCount('DetailGpsTrackerMap')
    hasCountedLoadRef.current = true
  }, [isLoaded])

  if (loadError) {
    return <div>Map cannot be loaded right now, sorry.</div>
  }

  if (!isLoaded) {
    return <div>地図を読み込み中...</div>
  }

  return (
    <div className="detail-modal-content-map">
      <div className="map-view">
        <GoogleMap
          mapContainerStyle={{ width: '100%', height: '400px' }}
          center={mapCenter}
          zoom={14}
          onLoad={() => {
            updateOffsetSize()
          }}
        >
          {/* 移動経路を線で表示 */}
          {polylinePath.length > 1 && (
            <Polyline
              path={polylinePath}
              options={{
                strokeColor: '#1F95F4',
                strokeOpacity: 0.77,
                strokeWeight: 10,
              }}
            />
          )}

          {/* 各ポイントにマーカーを配置 */}
          {(activeCoordinate || statusCoordinate) && (
            <Marker
              position={activeCoordinate
                ? { lat: activeCoordinate.lat, lng: activeCoordinate.lng }
                : { lat: statusCoordinate!.lat, lng: statusCoordinate!.lng }}
              icon={{
                url: firstMapPinIcon,
                scaledSize: new window.google.maps.Size(48, 48),
              }}
              onClick={() => activeIndex !== null && handleMarkerClick(activeIndex)}
            >
              {/* InfoWindow */}
              {showInfoWindow && infoWindowPosition && (
                <InfoWindow
                  options={infoWindowOptions}
                  position={infoWindowPosition}
                  onCloseClick={() => { setShowInfoWindow(false) }}
                >
                  <div>
                    <span>
                      {activeCoordinate
                        ? getFromNowAsText(activeCoordinate.timestamp)
                        : statusCoordinate && getFromNowAsText(statusCoordinate.timestamp)}
                    </span>
                    {(statusCoordinate && currentStatus?.gps_satellites) && (
                      <>
                        <br />
                        <span>
                          衛星数:
                          {currentStatus.gps_satellites}
                          個
                        </span>
                        <br />
                      </>
                    )}
                    <a
                      target="_blank"
                      rel="noopener noreferrer"
                      href={`https://www.google.com/maps/search/?api=1&query=${infoWindowPosition.lat},${infoWindowPosition.lng}`}
                    >
                      <span>Googleマップで見る</span>
                    </a>
                  </div>
                </InfoWindow>
              )}
            </Marker>
          )}
        </GoogleMap>
      </div>

      {coordinates.length > 1 && activeCoordinate && activeIndex !== null && (
        <div className="log-info">
          <div className="slider-container" style={{ paddingLeft: '20px', paddingRight: '20px' }}>
            <input
              type="range"
              min={minTimestamp}
              max={maxTimestamp}
              value={sliderValue}
              onChange={(e) => {
                const targetValue = parseInt(e.target.value, 10)
                const closestIndex = coordinates.reduce((closest, coord, index) => {
                  const coordTime = new Date(coord.timestamp).getTime()
                  const closestTime = new Date(coordinates[closest]!.timestamp).getTime()
                  return Math.abs(coordTime - targetValue) < Math.abs(closestTime - targetValue) ? index : closest
                }, 0)
                handleMarkerClick(closestIndex)
              }}
              style={{ maxWidth: '100%', padding: '0px' }}
              className="time-slider"
            />
            <div className="slider-label" style={{ marginLeft: `calc(${sliderPercentage}%)` }}>
              {sliderLabel}
            </div>
          </div>
          {detailHistoryEnabled && (
            <div className="date-conatiner">
              <label>日付</label>
              <input
                className="date"
                value={date}
                type="date"
                onChange={(e) => {
                  updateGpsTrackerLogs(e.target.value).catch(() => {
                    // updateGpsTrackerLogs内でエラーハンドリング済み
                  })
                }}
              />
              {jt808Logs.length > 0 ? (
                <button type="button" className="btn btn-primary btn-sm" onClick={downloadLogs}>
                  CSVをダウンロード(
                  {jt808Logs.length}
                  件)
                </button>
              ) : (
                <button type="button" disabled className="btn btn-primary btn-sm">
                  ログがありません
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default DetailGpsTrackerMap
